/**
 * Per-tenant service worker: /s/{slug}/sw.js with scope /s/{slug}/.
 * The worker file is the same shared build copied into each tenant folder,
 * so every studio gets its own scope and its own cache names.
 */
export function tenantBase(slug: string): string {
  return `/s/${slug}/`;
}

export async function registerTenantWorker(slug: string): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return null;
  try {
    return await navigator.serviceWorker.register(`${tenantBase(slug)}sw.js`, { scope: tenantBase(slug), updateViaCache: 'none' });
  } catch {
    return null;
  }
}

/** In dev (or for a studio whose static shell is not deployed yet) make sure the head points at the tenant files. */
export function ensureTenantHead(slug: string, name: string, accent: string): void {
  const base = tenantBase(slug);
  if (!document.querySelector('link[rel="manifest"]')) {
    const l = document.createElement('link');
    l.rel = 'manifest';
    l.href = `${base}manifest.webmanifest`;
    document.head.appendChild(l);
  }
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = '#050506';
  document.documentElement.style.setProperty('--tenant-accent', accent);
  if (!document.title || document.title === 'app') document.title = name;
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
